import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type OverlayDialogProps = {
  title: string;
  kicker?: string;
  onClose: () => void;
  children: ReactNode;
  closeLabel?: string;
};

export function OverlayDialog({ title, kicker, onClose, children, closeLabel = 'Tutup' }: OverlayDialogProps) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="bkux-modal"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="bkux-modal__surface" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="bkux-modal__header">
          <div className="bkux-modal__title">
            {kicker ? <span className="page-kicker">{kicker}</span> : null}
            <h2 id={titleId}>{title}</h2>
          </div>
          <button ref={closeRef} className="bkux-modal__close" type="button" onClick={onClose} aria-label={closeLabel}>
            <span aria-hidden="true">×</span>
            <span className="bkux-modal__close-label">{closeLabel}</span>
          </button>
        </header>
        <div className="bkux-modal__body">{children}</div>
      </section>
    </div>,
    document.body,
  );
}
